// EmployeeLocationMap.js
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";

// Fix marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
const liveIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [1, -34],
});

const offlineIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [1, -34],
});

// Re-center map when location changes
const MapRecenter  = ({ position }) => {
  const map = useMap();
  const hasCenteredRef = useRef(false);

  useEffect(() => {
    if (!hasCenteredRef.current && position) {
      map.setView(position, 15);
      hasCenteredRef.current = true;
    }
  }, [position, map]);

  return null;
};


const EmployeeLocationMap = ({
  employeeMapData,
  maxheight = 300,
}) => {
  const markerRef = useRef(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const lat = employeeMapData?.lat;
  const lng = employeeMapData?.lng;
  const name = employeeMapData?.name;
  const time = employeeMapData?.time;
  const timestamp = employeeMapData?.timestamp;

  const position = lat && lng ? [Number(lat), Number(lng)] : [20.5937, 78.9629];

    // Auto-open popup when data changes
  useEffect(() => {
    if (!lat || !lng) return;

    const timer = setTimeout(() => {
      markerRef.current?.openPopup();
    }, 100);

    return () => clearTimeout(timer);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamp]);

  // Reverse geocode when location changes
  useEffect(() => {
    if (!lat || !lng) return;

    const fetchAddress = async () => {
      if(employeeMapData?.address) {
        setAddress(employeeMapData?.address);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
          {
            headers: {
              "User-Agent": "Meepl-HRMS/1.0 (support@meepl.com)"
            }
          }
        );
        if (!res.ok) {
          setAddress("Unable to fetch location name");
          return;
        }
        const text = await res.text();

        try {
          const data = JSON.parse(text);
          setAddress(data?.display_name || "Location not found");
          return;
        } catch (parseErr) {
          console.log("Invalid JSON response:", text);
          return;
        }
      } catch {
        setAddress("Unable to fetch location name");
      } finally {
        setLoading(false);
      }
    };

    fetchAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 5000);

    return () => clearInterval(interval);
  }, []);


  let isLive = employeeMapData?.timestamp && now - employeeMapData.timestamp <= 35000; // 35 seconds

  return (
    <Box>
      <MapContainer
        center={position}
        zoom={15}
        style={{ height: maxheight, width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <MapRecenter position={position} />

        <Marker
          position={position}
          ref={markerRef}
          icon={isLive ? liveIcon : offlineIcon}
        >
          <Popup>
            <Box sx={{ fontSize: 13, maxWidth: 240 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <strong>{name}</strong>
                <Box
                  sx={{
                    backgroundColor: isLive ? "success.main" : "#fd7567",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                  }}
                />
                <Box>{isLive ? "Online" : "Offline"}</Box>
              </Box>
              Last Update: {time || new Date(timestamp).toLocaleTimeString()}
              <br />
              <br />
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={14} />
                  Fetching location name…
                </Box>
              ) : (
                address
              )}
              <br />
            </Box>
          </Popup>
        </Marker>
      </MapContainer>
    </Box>
  );
};

export default EmployeeLocationMap;
